import { clientLogger } from '@/lib/clientLogger';
'use client'

import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { User } from 'firebase/auth';
import { authService } from '@/lib/firebase';
import { apiService } from '@/lib/api';
import { isNative } from '@/lib/capacitor';
import { cleanupOnLogout } from '@/lib/locationService';
import { cleanupTerminal } from '@/lib/tapToPay';
import { clearClientSessionData } from '@/lib/session';
import { initializePushNotifications, unregisterPushNotifications } from '@/lib/pushNotificationService';

export const useAuth = () => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      setUser(user);
      setIsAuthenticated(!!user);
      setIsLoading(false);

      if (user) {
        // Store the token when user is authenticated
        // Firebase automatically handles token refresh, so we get a fresh token
        const token = await user.getIdToken();
        apiService.setToken(token);
        // Location tracking is started from GlobalOrganizationProvider when org is selected and role is technician
        // Initialize push notifications for native platforms
        initializePushNotifications().catch((error) => {
          clientLogger.error('[Auth] Failed to initialize push notifications:', error);
        });
      } else {
        // Clear token when user is not authenticated
        apiService.clearToken();

        // On native, check if this was an unexpected logout (IndexedDB cleared)
        if (isNative()) {
          const wasUnexpected = await authService.wasAuthUnexpectedlyCleared();
          if (wasUnexpected) {
            clientLogger.warn('[Auth] Firebase auth was unexpectedly cleared (IndexedDB may have been wiped). User must re-login.');
            // Clear the backup flag so we don't keep detecting this
            await authService.clearAuthBackup();
          }

          // signOut() already did this; covers auth dropping without it (e.g. cleared storage).
          unregisterPushNotifications().catch((error) => {
            clientLogger.error('[Auth] Failed to unregister push notifications:', error);
          });

          clientLogger.info('[Auth] User logged out, cleaning up location tracking');
          cleanupOnLogout().catch((error) => {
            clientLogger.error('[Auth] Failed to cleanup location tracking:', error);
          });

          // The Tap to Pay reader is warmed in the background and outlives any
          // single screen, so logging out is what has to drop it — otherwise it
          // stays connected under the previous user's Stripe account.
          cleanupTerminal().catch((error) => {
            clientLogger.error('[Auth] Failed to disconnect Tap to Pay reader:', error);
          });
        }
      }
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const result = await authService.signIn(email, password);
      // Token is already set in the auth state change handler
      // Location tracking starts from GlobalOrganizationProvider when org is selected (technician)
      return result;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      await unregisterPushNotifications();
      await authService.signOut();
      await clearClientSessionData(queryClient);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const checkToken = async () => {
    const token = apiService.getToken();
    return !!token;
  };

  const refreshToken = async () => {
    if (user) {
      try {
        // Force refresh the token
        const token = await user.getIdToken(true);
        apiService.setToken(token);
        return token;
      } catch (error) {
        clientLogger.error('Failed to refresh token:', error);
        throw error;
      }
    }
    return null;
  };

  return {
    user,
    isLoading,
    isAuthenticated,
    signIn,
    signOut,
    checkToken,
    refreshToken,
  };
};